// @react
import React, { ReactElement, useContext, useState } from 'react'
// @types
import { GetUserQueryResult } from 'lib/graphql/graphqlTypes'
import { ContactFormValues } from 'modules/common/forms/types/contact/'
// @graphQL
// @libraries
import { DevTool } from '@hookform/devtools'
// @context
import { PersonalInformationContext } from 'modules/profile/context'
// @components
import Grid from '@bit/atd.web.grid'
import Banner from '@bit/atd.web.banner'
import Divider from '@bit/atd.web.divider'
import useFormWrapper from 'modules/common/forms/hooks/useContactFormWrapper'
// @components to migrate
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import ReadOnlyFields from 'modules/common/forms/fields/contact/ReadOnly'
import EmailField from 'modules/common/forms/fields/contact/Email'
import PhoneFieldsArray from 'modules/common/forms/fields/contact/Phones'
import AddressFieldsArray from 'modules/common/forms/fields/contact/Address'
import EditAction from 'modules/profile/components/EditAction'
import EditingAction from 'modules/profile/components/EditingAction'
import TextLink from '@bit/atd.web.text-link/dist/TextLink'
import Typography from '@material-ui/core/Typography'
import Spacer from '@bit/atd.web.spacer'
import Helper from '../../../../../utils/Helper.class'
import { ExternalRouteNames } from '../../../../../constants/routeNames'
import strings from '../../../../../lib/constants/strings'

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: () => void
}

const ContactForm = ({
  data: userData,
  isLoading,
  refetch,
}: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [editing, setEditing] = useState(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************************
   * HOOKS
   *****************************************/

  const profileInformation = useContext(PersonalInformationContext)
  const {
    formMethods,
    control,
    userContactInfoUpdateHandler,
    userAddressDeleteHandler,
    userPhoneDeleteHandler,
  } = useFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })

  // React Hook Form
  const {
    formState: { dirtyFields, errors },
  } = formMethods
  const isFormDirty = Object.keys(dirtyFields).length > 0

  /****************************************
   * FORM HANDLERS
   *****************************************/

  /**
   *
   */
  const onEditHandler = () => {
    profileInformation.setEditingSection('information')
    setEditing(true)
  }

  /**
   *
   */
  const onCancelHandler = () => {
    profileInformation.setEditingSection(null)
    setEditing(false)
    formMethods.clearErrors()
    formMethods.reset()
  }

  /**
   *
   * @param id
   * @param type
   */
  const onRemoveHandler = async (
    id: number,
    type: 'phone' | 'address',
  ): Promise<boolean> => {
    setIsSubmitting(true)
    switch (type) {
      case 'phone': {
        const res = await userPhoneDeleteHandler(id)
        setIsSubmitting(false)
        // @todo this is probably a bit crude to cancel the whole form on remove item
        // we delete mutation item right away on click remove, instead of waiting for save
        // it's going to get too complex for now if we gather all deletes, edits + additions before onSave
        // however we probably want an 'are you sure' popup for delete valid item
        // i.e. --> click remove on valid item
        // check if item has valid ID (not index, but entity ID like addressID)
        //
        // --> (item with saved data) modal 'are you sure'
        // --> submit mutation
        // --> remove() formArray item,
        // onCancelHandler()
        // if NOT valid item (i.e. empty or unsaved item w/ no entity id ), remove button should remove from fieldArray only
        // no modal
        // stay in form edit mode
        //onCancelHandler()
        return res
      }
      case 'address': {
        const res = await userAddressDeleteHandler(id)
        setIsSubmitting(false)
        // @todo this is probably a bit crude to cancel the whole form on remove item
        // i've used this here because now we get validation error on required fields when removing empty item
        //onCancelHandler()
        return res
      }
    }
    return false
  }

  const onSubmitHandler = async (data: ContactFormValues) => {
    if (!isFormDirty) {
      onCancelHandler()
      return
    }

    setIsSubmitting(true)
    const res = await userContactInfoUpdateHandler(data)
    setIsSubmitting(false)
    if (res) {
      profileInformation.setEditingSection(null)
      setEditing(false)
    }
  }

  /****************************************
   * RENDER
   *****************************************/

  const form = (
    <>
      <form>
        {process.env.DEBUG !== '1' && <DevTool control={control} />}

        <Grid container spacing={4}>
          {/* Banner */}
          <Grid xs={12} item>
            <Banner fullWidth icon="lock-on">
              None of the following information will ever be visible on your
              public profile.
            </Banner>
          </Grid>

          {/* Read-Only Inputs */}
          {!editing && <ReadOnlyFields data={userData} />}

          {/* Form */}
          {editing && (
            <>
              <PhoneFieldsArray
                formMethods={formMethods}
                control={control}
                errors={errors}
                onRemove={(id) => onRemoveHandler(id, 'phone')}
              />

              <Grid xs={12} item>
                <Divider width="100%" color="#DBD9D6" height="1px" />
              </Grid>

              <Grid xs={12} item>
                <EmailField control={control} errors={errors} />
                <div>
                  <Spacer size={2} />
                </div>
                <TextLink
                  variant="primary"
                  href={Helper.getPathFromRouteKey('PROFILE_SECURITY')}
                >
                  Click here to update your email.
                </TextLink>
              </Grid>

              <Grid xs={12} item>
                <Divider width="100%" color="#DBD9D6" height="1px" />
              </Grid>

              <AddressFieldsArray
                formMethods={formMethods}
                control={control}
                errors={errors}
                onRemove={(id) => onRemoveHandler(id, 'address')}
              />
            </>
          )}
        </Grid>
      </form>
    </>
  )

  const box = (content: ReactElement): ReactElement => {
    return (
      <>
        <Box
          header={{
            title: !editing
              ? 'Contact Information'
              : 'Editing Contact Information',
            actions: !editing ? (
              <EditAction
                disabled={isLoading}
                onClick={() => onEditHandler()}
              />
            ) : (
              <>
                <EditingAction
                  bottom
                  isSubmitting={isMutationSubmitting}
                  onCancel={() => onCancelHandler()}
                  onSubmit={formMethods.handleSubmit((data) =>
                    onSubmitHandler(data),
                  )}
                />
              </>
            ),
          }}
        >
          {content}
        </Box>
      </>
    )
  }

  return !isLoading ? box(form) : box(<SkeletonLoader />)
}

export default ContactForm
